import { ReactNode, forwardRef } from 'react'
import styled from '@emotion/styled'
import { SpanLabel } from 'modules/ui/atoms/Typography/Label'
import { PaymentIcon } from 'modules/ui/atoms/Icon'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { RadioButtonProps } from 'modules/ui/atoms/RadioButton'
import RadioButtonContainer from 'modules/ui/atoms/RadioButton/RadioButtonContainer'
import RadioButtonInput from 'modules/ui/atoms/RadioButton/RadioButtonInput'
import useConfig from 'modules/config/state/use-config'

export interface PaymentSelectorItemProps extends RadioButtonProps {
  icon: string
  secondaryItems?: ReactNode
  accordionContent?: ReactNode
}

const PaymentSelectorItem = forwardRef<HTMLLabelElement, PaymentSelectorItemProps>(
  (
    {
      value,
      checked,
      onChange,
      radioGroupName,
      disabled,
      label = '',
      icon,
      secondaryItems,
      accordionContent,
    }: PaymentSelectorItemProps,
    ref,
  ) => {
    const config = useConfig()
    const isCheckoutCampaign = !config.isEmbedded

    const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()

        if (!disabled) {
          onChange(value)
        }
      }
    }

    return (
      <>
        <PaymentSelectorItemContainer
          ref={ref}
          checked={checked}
          disabled={disabled}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          role="radio"
          aria-checked={checked}
          aria-disabled={disabled}
          isCheckoutCampaign={isCheckoutCampaign}
        >
          <RadioButtonInput
            id={`radio-button-${value}`}
            data-id={`payment-method-checkbox-${value?.toLowerCase()}`}
            type="radio"
            value={value}
            checked={checked}
            onChange={() => onChange(value)}
            disabled={disabled}
            name={radioGroupName}
          />
          <SpanLabel>
            <PaymentSelectorItemLabel label={label} icon={icon} />
          </SpanLabel>
          <span style={{ marginLeft: 'auto' }}>{secondaryItems}</span>
        </PaymentSelectorItemContainer>
        {checked && accordionContent && isCheckoutCampaign && (
          <AccordionContentContainer isCheckoutCampaign={isCheckoutCampaign}>
            {accordionContent}
          </AccordionContentContainer>
        )}
      </>
    )
  },
)

PaymentSelectorItem.displayName = 'PaymentSelectorItem'

export default PaymentSelectorItem

const PaymentSelectorItemContainer = styled(RadioButtonContainer)<{ isCheckoutCampaign?: boolean }>(
  ({ isCheckoutCampaign, ...props }) => ({
    padding: '16px 20px 16px 16px',
    borderBottom: `1px solid ${DEFAULT_THEME_COLORS['--theme-color--neutral-90']}`,
    backgroundColor: isCheckoutCampaign
      ? 'transparent'
      : props.checked
      ? DEFAULT_THEME_COLORS['--theme-color--neutral-90']
      : DEFAULT_THEME_COLORS['--theme-color--neutral-100'],
    '.card-icons': {
      opacity: props.checked ? 1 : 0.7,
    },
  }),
)

const AccordionContentContainer = styled.div<{ isCheckoutCampaign?: boolean }>`
  background-color: ${({ isCheckoutCampaign }) =>
    isCheckoutCampaign ? 'transparent' : DEFAULT_THEME_COLORS['--theme-color--neutral-90']};
  border-bottom: 1px solid ${DEFAULT_THEME_COLORS['--theme-color--neutral-90']};
  padding: 0 16px 16px 16px;
`

const PaymentSelectorItemLabel = ({ label, icon }: { label: string; icon: string }) => (
  <>
    <span
      aria-hidden="true"
      style={{ width: '36px', display: 'flex', marginRight: '8px', justifyContent: 'center' }}
    >
      <PaymentIcon name={icon} />
    </span>
    {label}
  </>
)
